<template>
  <div>
    <MMP_DRT_AddError
      v-if="this.error"
      :customerProjectId="customerProjectId"
      :drt_releaseId="drt_releaseId"
      :errLat="this.errLat"
      :errLayer="this.errLayer"
      :errLong="this.errLong"
      :map="map"
      :releases="this.releases"
      :selectedDataset="this.selectedDataset"
      :severityOptions="this.severityOptions"
      :statusOptions="this.statusOptions"
      :user="this.user"
      @refresh="$emit('refresh', $event)"
      @notification="$emit('notification', $event)"
    ></MMP_DRT_AddError>
    <MMP_DRT_UpdateError
      :severityOptions="this.severityOptions"
      :statusOptions="this.statusOptions"
      :updateErrorDataSource="updateErrorDataSource"
      :userEmail="this.user.email"
      :user="this.user"
      @refresh="$emit('refresh', $event)"
      @notification="$emit('notification', $event)"
    ></MMP_DRT_UpdateError>
    <MMP_DRT_ErrorManager
      :customerProjectId="customerProjectId"
      :map="map"
    ></MMP_DRT_ErrorManager>
    <!-- Error Control Toolbar -->
    <div v-if="this.errorsLayerVisibility && !panelClosed" class="error-control-toolbar">
      <!-- Title -->
      <div class="ec-title">
        <span class="ec-title-badge">
          <fa :icon="['fas', 'exclamation-triangle']" class="ec-title-icon" />
        </span>
        <span class="ec-title-text">Error Controls</span>
      </div>

      <div class="ec-separator"></div>

      <!-- Segmented mode toggle: Add / Update are mutually exclusive -->
      <div class="ec-segment" role="group" aria-label="Error edit mode">
        <button
          type="button"
          class="ec-seg-btn"
          :class="{ 'ec-seg-btn--active': this.error }"
          :disabled="this.updateError"
          @click="$emit('toggleError')"
        >
          <fa :icon="['fas', 'plus']" class="ec-seg-icon" /> Add
        </button>
        <button
          type="button"
          class="ec-seg-btn"
          :class="{ 'ec-seg-btn--active': this.updateError }"
          :disabled="this.error"
          @click="$emit('toggleUpdateError')"
        >
          <fa :icon="['fas', 'pencil-alt']" class="ec-seg-icon" /> Update
        </button>
      </div>

      <!-- Live status: shows only while a mode is armed -->
      <transition name="ec-status">
        <div v-if="error || updateError" class="ec-status">
          <span class="ec-status-dot"></span>
          <span class="ec-status-text">{{ error ? 'Click the map to place an error' : 'Select an error to update' }}</span>
        </div>
      </transition>

      <div class="ec-separator"></div>

      <!-- Export menu: Shapefile / CSV are two flavors of one action -->
      <b-dropdown
        dropup
        right
        boundary="window"
        size="sm"
        variant="link"
        toggle-class="ec-export-toggle"
        menu-class="ec-export-menu"
        no-caret
      >
        <template #button-content>
          <fa :icon="['fas', 'download']" class="ec-seg-icon" /> Export
          <fa :icon="['fas', 'chevron-up']" class="ec-caret" />
        </template>
        <h6 class="ec-menu-caption">Download as</h6>
        <b-dropdown-item-button @click="$emit('exportFile', 'shape')">
          <span class="ec-menu-chip ec-menu-chip--shape">
            <fa :icon="['fas', 'layer-group']" />
          </span>
          <span class="ec-menu-item-text">
            <span class="ec-menu-item-title">Shapefile</span>
            <span class="ec-menu-item-sub">.shp geometry bundle</span>
          </span>
        </b-dropdown-item-button>
        <b-dropdown-item-button @click="$emit('exportFile', 'csv')">
          <span class="ec-menu-chip ec-menu-chip--csv">
            <fa :icon="['fas', 'table']" />
          </span>
          <span class="ec-menu-item-text">
            <span class="ec-menu-item-title">CSV</span>
            <span class="ec-menu-item-sub">Spreadsheet table</span>
          </span>
        </b-dropdown-item-button>
      </b-dropdown>
<!-- 
      <button class="ec-close" title="Close" @click="panelClosed = true">
        <fa :icon="['fas', 'times']" />
      </button> -->
    </div>
  </div>
</template>

<style scoped>
/* =========================
   HORIZONTAL TOOLBAR
   ========================= */
.error-control-toolbar {
  position: relative;
  display: inline-flex;
  flex-wrap: nowrap;
  width: max-content;
  align-items: center;
  gap: 10px;
  padding: 7px 9px 7px 14px;
  white-space: nowrap;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  box-shadow:
    0 18px 40px rgba(6, 60, 109, 0.28),
    0 4px 10px rgba(6, 60, 109, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  overflow: visible;
  transform-origin: bottom center;
  animation: ecToolbarRise 0.34s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.error-control-toolbar:hover {
  transform: translateY(-1px);
  box-shadow:
    0 22px 48px rgba(6, 60, 109, 0.32),
    0 6px 14px rgba(6, 60, 109, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

@keyframes ecToolbarRise {
  0% {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== Title ===== */
.ec-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #063c6d;
}

.ec-title-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.22), rgba(255, 152, 0, 0.16));
  box-shadow: inset 0 0 0 1px rgba(255, 152, 0, 0.35);
  flex-shrink: 0;
}

.ec-title-icon {
  font-size: 0.78rem;
  color: #d98200;
}

.ec-title-text {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #063c6d;
}

.ec-separator {
  width: 1px;
  align-self: stretch;
  margin: 4px 0;
  background: rgba(6, 60, 109, 0.14);
}

/* ===== Segmented mode toggle ===== */
.ec-segment {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(6, 60, 109, 0.07);
  box-shadow: inset 0 0 0 1px rgba(6, 60, 109, 0.1);
}

.ec-seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border: none;
  background: transparent;
  color: #063c6d;
  font-size: 0.74rem;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease;
}

.ec-seg-btn:hover:not(:disabled):not(.ec-seg-btn--active) {
  background: rgba(6, 60, 109, 0.12);
}

.ec-seg-btn:active:not(:disabled) {
  transform: scale(0.96);
}

.ec-seg-btn--active {
  background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.ec-seg-btn:focus-visible {
  outline: 2px solid rgba(10, 84, 143, 0.5);
  outline-offset: 1px;
}

.ec-seg-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ec-seg-icon {
  font-size: 0.7rem;
}

/* ===== Live active-mode status ===== */
.ec-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 12px 4px 10px;
  border-radius: 999px;
  background: rgba(10, 84, 143, 0.08);
  color: #0a548f;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.ec-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0a548f;
  box-shadow: 0 0 0 0 rgba(10, 84, 143, 0.5);
  animation: ecPulse 1.5s ease-out infinite;
  flex-shrink: 0;
}

@keyframes ecPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(10, 84, 143, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(10, 84, 143, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 84, 143, 0);
  }
}

/* Slide/fade the status chip in and out as modes toggle */
.ec-status-enter-active,
.ec-status-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-width 0.25s ease;
  overflow: hidden;
}
.ec-status-enter,
.ec-status-leave-to {
  opacity: 0;
  max-width: 0;
  transform: translateX(-6px);
}
.ec-status-enter-to,
.ec-status-leave {
  max-width: 260px;
}

/* ===== Export dropdown ===== */
::v-deep(.ec-export-toggle) {
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
  padding: 6px 14px !important;
  border: 1px solid rgba(6, 60, 109, 0.2) !important;
  border-radius: 999px !important;
  background: #ffffff !important;
  color: #063c6d !important;
  font-size: 0.74rem !important;
  font-weight: 600 !important;
  text-decoration: none !important;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

::v-deep(.ec-export-toggle:hover),
::v-deep(.show > .ec-export-toggle) {
  background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%) !important;
  color: #ffffff !important;
  border-color: transparent !important;
  box-shadow: 0 3px 10px rgba(6, 60, 109, 0.3) !important;
}

::v-deep(.show > .ec-export-toggle .ec-caret) {
  transform: rotate(180deg);
}

.ec-caret {
  font-size: 0.6rem;
  opacity: 0.8;
  transition: transform 0.18s ease;
}

::v-deep(.ec-export-menu) {
  min-width: 12rem;
  border: 1px solid rgba(6, 60, 109, 0.12);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(6, 60, 109, 0.24);
  padding: 6px;
  animation: ecMenuPop 0.16s ease-out both;
  /* Small nudge up + left; margins don't override Popper's transform */
  margin-top: -20px;
  margin-left: -28px;
}

/* Animate opacity ONLY — the menu's `transform` is owned by Popper for
   positioning; animating it here would override placement and clip the menu. */
@keyframes ecMenuPop {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

::v-deep(.ec-menu-caption) {
  margin: 2px 0 4px;
  padding: 2px 10px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8ba6bd;
}

::v-deep(.ec-export-menu .dropdown-item) {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  color: #063c6d;
  padding: 7px 10px;
}

::v-deep(.ec-export-menu .dropdown-item:hover) {
  background: rgba(6, 60, 109, 0.07);
  color: #063c6d;
}

::v-deep(.ec-export-menu .dropdown-item:active) {
  background: rgba(6, 60, 109, 0.12);
  color: #063c6d;
}

.ec-menu-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 0.78rem;
  flex-shrink: 0;
}

.ec-menu-chip--shape {
  background: rgba(10, 84, 143, 0.12);
  color: #0a548f;
}

.ec-menu-chip--csv {
  background: rgba(22, 163, 74, 0.14);
  color: #16a34a;
}

.ec-menu-item-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.ec-menu-item-title {
  font-size: 0.8rem;
  font-weight: 600;
}

.ec-menu-item-sub {
  font-size: 0.66rem;
  color: #8ba6bd;
  font-weight: 500;
}

/* ===== Close ===== */
.ec-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: rgba(6, 60, 109, 0.06);
  color: #063c6d;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.7rem;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.ec-close:hover {
  background: #d64545;
  color: #ffffff;
  transform: rotate(90deg);
}
</style>

<script>
import MMP_DRT_AddError from './MMP_DRT_AddError.vue'
import MMP_DRT_ErrorManager from './MMP_DRT_ErrorManager.vue'
import MMP_DRT_UpdateError from './MMP_DRT_UpdateError.vue'
import axios from 'axios'
export default {
  name: 'MMP_DRT_ErrorControl',
  components: {
    MMP_DRT_AddError,
    MMP_DRT_ErrorManager,
    MMP_DRT_UpdateError
  },
  props: [
    'customerProjectId',
    'drt_releaseId',
    'error',
    'errorsLayerVisibility',
    'errLat',
    'errLayer',
    'errLong',
    'legendImgSrc',
    'map',
    'releases',
    'selectedDataset',
    'updateError',
    'updateErrorDataSource',
    'user'
  ],
  data() {
    return {
      panelClosed: false,
      severityOptions: [
        {text: 'Select Severity', value: null, disabled: true}
      ],
      statusOptions: [{text: 'Select Status', value: null, disabled: true}]
    }
  },
  watch: {
    // Re-open the panel whenever the errors layer is toggled back on.
    errorsLayerVisibility(newVal) {
      if (newVal) {
        this.panelClosed = false
      }
    }
  },
  methods: {
    booleanBtnClass(btn) {
      // btn is a passed in variable, either error or updateError
      return btn ? 'primary' : 'info'
    },
    // REFACTOR: again feels like this could be two different functions
    getOptions() {
      if (sessionStorage.token) {
        // Populates this.severityOptions
        axios
          .get(process.env.API_LOCATION + '/api/error/severityOptions', {
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Severity Options API Call Error: ' + content.errors)
            } else {
              content.forEach(element => {
                this.severityOptions.push({
                  text: element.severity,
                  value: element.id
                })
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })

        // Populates this.statusOptions
        axios
          .get(process.env.API_LOCATION + '/api/error/statusOptions', {
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Status Options API Call Error: ' + content.errors)
            } else {
              content.forEach(element => {
                this.statusOptions.push({
                  text: element.status,
                  value: element.id
                })
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        this.$router.push({name: 'Login'})
      }
    }
  },
  mounted() {
    this.getOptions()
  },
  updated() {
    if (!this.errorsLayerVisibility) {
      if (this.error) {
        this.$emit('toggleError')
      }
      if (this.updateError) {
        this.$emit('toggleUpdateError')
      }
    }
  }
}
</script>
